import * as Sentry from '@sentry/react';
import { environment } from '../../environment';

interface LogContext {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, any>;
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
  fingerprint?: string[];
}

type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

/**
 * Gets user timezone with fallback
 */
const getTimezone = (): string => {
  try {
    return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Gets screen dimensions with fallback
 */
const getScreenDimensions = (): { width: number; height: number } => {
  return {
    width: window.screen?.width || 0,
    height: window.screen?.height || 0,
  };
};

/**
 * Gets device info (memory, CPU cores) - Chrome/Edge only
 */
const getDeviceInfo = (): {
  memory?: number;
  hardwareConcurrency?: number;
} => {
  const deviceInfo: { memory?: number; hardwareConcurrency?: number } = {};

  try {
    const nav = navigator as any;
    if (typeof nav.deviceMemory !== 'undefined') {
      deviceInfo.memory = nav.deviceMemory;
    }

    if (typeof navigator.hardwareConcurrency !== 'undefined') {
      deviceInfo.hardwareConcurrency = navigator.hardwareConcurrency;
    }
  } catch {
    // Return empty object if anything fails
  }

  return deviceInfo;
};

/**
 * Enriches Sentry event with browser context
 */
const enrichEventWithContext = (
  event: Sentry.ErrorEvent
): Sentry.ErrorEvent | null => {
  try {
    const deviceInfo = getDeviceInfo();

    // Only add device context if we have at least one property
    if (Object.keys(deviceInfo).length > 0) {
      event.contexts = {
        ...event.contexts,
        device: deviceInfo,
      };
    }
  } catch (error) {
    // If anything fails, just return the event as-is
    // Don't let context enrichment break error reporting
    if (environment.isDevelopment) {
      console.debug('[Logger] Failed to enrich error context:', error);
    }
  }

  return event;
};

export class Logger {
  private readonly DISABLE_CONSOLE_LOGS = !environment.isDevelopment;
  private readonly SEND_TO_SENTRY_LEVELS: Set<LogLevel> = new Set<LogLevel>([
    'warning',
    'error',
    'fatal',
  ]);

  // Simple Set to track errors already sent (cleared on page refresh)
  private sentErrors: Set<string> = new Set();

  // Track recent breadcrumbs to prevent duplicates
  private recentBreadcrumbs: Array<{
    message: string;
    category: string;
    data?: Record<string, any>;
  }> = [];
  private readonly MAX_RECENT_BREADCRUMBS = 3;

  // Track if Sentry was initialized successfully
  public sentryInitialized = false;

  /**
   * Initializes Sentry with proper configuration
   */
  public init(): void {
    // Prevent multiple initializations
    if (this.sentryInitialized) {
      return;
    }

    if (!environment.isProduction) {
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.info(
          '[Logger] Skipping Sentry init in non-production environment'
        );
      }
      return;
    }

    try {
      const screenDimensions = getScreenDimensions();
      const timezone = getTimezone();

      Sentry.init({
        dsn: environment.sentryConfiguration.dsn,
        environment: environment.env,
        release: `blocksite-web-app@${process.env.APP_VERSION || 'unknown'}`,

        sampleRate: environment.isProduction ? 0.1 : 1.0,

        tracesSampleRate: 0,

        ignoreErrors: [
          'top.GLOBALS',
          'chrome-extension://',
          'moz-extension://',
          'Network request failed',
          'NetworkError',
          'Failed to fetch',
          'AbortError',
          'User denied',
        ],

        defaultIntegrations: false,

        integrations: [
          Sentry.dedupeIntegration(),
          Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true,
          }),
          Sentry.httpContextIntegration(),
          Sentry.linkedErrorsIntegration(),
          Sentry.breadcrumbsIntegration({
            console: false,
            dom: false,
            fetch: false,
            history: false,
            sentry: false,
            xhr: false,
          }),
        ],

        initialScope: {
          tags: {
            'user.timezone': timezone,
            'screen.width': screenDimensions.width,
            'screen.height': screenDimensions.height,
          },
        },

        beforeSend: event => enrichEventWithContext(event as Sentry.ErrorEvent),
      });

      this.sentryInitialized = true;
    } catch (error) {
      this.sentryInitialized = false;
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.error('[Logger] Failed to initialize Sentry:', error);
      }
    }
  }

  /**
   * Sets user context for all subsequent error reports
   */
  public setUser(
    user: { id?: string; email?: string; username?: string } | null
  ): void {
    if (!this.sentryInitialized) {
      return;
    }

    try {
      Sentry.setUser(user);
    } catch (error) {
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.error('[Logger] Failed to set user:', error);
      }
    }
  }

  /**
   * Checks if a similar breadcrumb was recently added
   * Uses local cache instead of Sentry scope for reliability
   */
  private isBreadcrumbDuplicate(
    message: string,
    category: string,
    data?: Record<string, any>
  ): boolean {
    return this.recentBreadcrumbs.some(breadcrumb => {
      // Match by message and category
      if (breadcrumb.message !== message || breadcrumb.category !== category) {
        return false;
      }

      // If data exists, compare key data properties
      if (data && breadcrumb.data) {
        const dataKeys = Object.keys(data);
        const breadcrumbDataKeys = Object.keys(breadcrumb.data);

        // If different number of keys, not a duplicate
        if (dataKeys.length !== breadcrumbDataKeys.length) {
          return false;
        }

        // Check if all key-value pairs match
        return dataKeys.every(
          key => breadcrumb.data && breadcrumb.data[key] === data[key]
        );
      }

      // If no data on both, it's a duplicate
      return !data && !breadcrumb.data;
    });
  }

  /**
   * Adds breadcrumb for tracking user actions leading to errors
   * Automatically prevents duplicate breadcrumbs from being added
   */
  public addBreadcrumb(
    message: string,
    category: string,
    level: LogLevel = 'info',
    data?: Record<string, any>
  ): void {
    if (!this.sentryInitialized) {
      return;
    }

    try {
      // Prevent duplicate breadcrumbs
      if (this.isBreadcrumbDuplicate(message, category, data)) {
        return;
      }

      // Add to Sentry
      Sentry.addBreadcrumb({
        message,
        category,
        level,
        data,
        timestamp: Date.now() / 1000,
      });

      // Track in local cache
      this.recentBreadcrumbs.push({ message, category, data });

      // Keep only last N breadcrumbs
      if (this.recentBreadcrumbs.length > this.MAX_RECENT_BREADCRUMBS) {
        this.recentBreadcrumbs.shift();
      }
    } catch (error) {
      // Silent - breadcrumbs are not critical
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.debug('[Logger] Failed to add breadcrumb:', error);
      }
    }
  }

  /**
   * Debug level - local console only, never sent to Sentry
   */
  public debug(message: string, context?: Record<string, any>): void {
    if (!this.DISABLE_CONSOLE_LOGS) {
      console.debug(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Info level - local console only, never sent to Sentry
   */
  public info(message: string, context?: Record<string, any>): void {
    if (!this.DISABLE_CONSOLE_LOGS) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Warning level - logged to console and sent to Sentry
   */
  public warn(message: string, context?: LogContext): void {
    if (!this.DISABLE_CONSOLE_LOGS) {
      console.warn(`[WARN] ${message}`, context || '');
    }

    this.sendToSentry('warning', message, undefined, context);
  }

  /**
   * Error level - for handled errors with detailed context
   * Use this when you catch an error and want to report it with context
   */
  public error(
    message: string,
    error?: Error | unknown,
    context?: LogContext
  ): void {
    if (!this.DISABLE_CONSOLE_LOGS) {
      console.error(`[ERROR] ${message}`, error || '', context || '');
    }

    this.sendToSentry('error', message, error, context);
  }

  /**
   * Fatal level - for critical errors that break core functionality
   */
  public fatal(
    message: string,
    error?: Error | unknown,
    context?: LogContext
  ): void {
    if (!this.DISABLE_CONSOLE_LOGS) {
      console.error(`[FATAL] ${message}`, error || '', context || '');
    }

    this.sendToSentry('fatal', message, error, context);
  }

  /**
   * Sends log data to Sentry with proper formatting
   */
  private sendToSentry(
    level: LogLevel,
    message: string,
    error?: Error | unknown,
    context?: LogContext
  ): void {
    if (!this.SEND_TO_SENTRY_LEVELS.has(level)) {
      return;
    }

    // Check if Sentry is initialized (critical check!)
    if (!this.sentryInitialized) {
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.debug('[Logger] Sentry not initialized, skipping error report');
      }
      return;
    }

    // Simple rate limiting: send each error only once per session
    const fingerprint = this.generateFingerprint(message, error);
    if (this.sentErrors.has(fingerprint)) {
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.debug('[Logger] Error already sent, skipping:', fingerprint);
      }
      return;
    }

    // Mark as sent
    this.sentErrors.add(fingerprint);

    // Send to Sentry
    try {
      Sentry.withScope(scope => {
        // Set level
        scope.setLevel(level);

        // Set custom tags
        if (context?.tags) {
          Object.entries(context.tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }

        // Set extra context
        if (context?.extra) {
          Object.entries(context.extra).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }

        // Set user context if provided
        if (context?.user) {
          scope.setUser(context.user);
        }

        // Set custom fingerprint for better grouping
        if (context?.fingerprint) {
          scope.setFingerprint(context.fingerprint);
        } else {
          // Create automatic fingerprint based on message to prevent duplicates
          scope.setFingerprint([this.generateFingerprint(message, error)]);
        }

        // Add context to extra
        scope.setContext('logContext', {
          timestamp: new Date().toISOString(),
          environment: environment.env,
        });

        // Capture the appropriate type
        if (error instanceof Error) {
          // For Error objects, use captureException for better stack traces
          const enrichedError = new Error(`${message}: ${error.message}`);
          enrichedError.stack = error.stack;
          enrichedError.name = error.name;

          scope.setExtra('originalError', {
            message: error.message,
            name: error.name,
          });
          scope.setExtra('contextMessage', message);

          Sentry.captureException(enrichedError);
        } else if (error) {
          // For non-Error objects, convert to structured data
          scope.setExtra('errorData', error);
          Sentry.captureMessage(message, level);
        } else {
          // No error object, just message
          Sentry.captureMessage(message, level);
        }
      });
    } catch (sentryError) {
      if (!this.DISABLE_CONSOLE_LOGS) {
        console.error('[Logger] Failed to send to Sentry:', sentryError);
      }
    }
  }

  /**
   * Generates a consistent fingerprint for error grouping
   */
  private generateFingerprint(
    message: string,
    error?: Error | unknown
  ): string {
    const messagePart = message.toLowerCase().replace(/[^a-z0-9]/g, '-');

    if (error instanceof Error && error.stack) {
      // Use first line of stack trace for better grouping
      const stackLine = error.stack.split('\n')[1] || '';
      const stackPart = stackLine.trim().substring(0, 50);
      return `${messagePart}-${stackPart}`;
    }

    return messagePart;
  }

  /**
   * Clear sent errors (useful for testing or manual reset)
   */
  public clearSentErrors(): void {
    this.sentErrors.clear();
  }

  /**
   * Check if Sentry is initialized (useful for testing)
   */
  public isSentryInitialized(): boolean {
    return this.sentryInitialized;
  }
}

export const logger = new Logger();

// Export helper functions for testing
export const __testing__ = {
  getTimezone,
  getScreenDimensions,
  getDeviceInfo,
  enrichEventWithContext,
};
